import { lazy, Suspense, useEffect, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import StorefrontWrapper from "./components/StorefrontWrapper";
import { CartProvider } from "./contexts/CartContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SiteThemeProvider } from "./hooks/useSiteTheme";
import SeoHead from "./components/SeoHead";
import { trackPageView } from "./lib/fbPixel";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

function lazyRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise<never>(() => {});
    })
  );
}

const StorefrontChrome = lazyRetry(() => import("./components/StorefrontChrome"));

import IndexPage from "./pages/Index";
const loadProductDetail = () => import("./pages/ProductDetail");
const loadAuth = () => import("./pages/Auth");
const loadCheckout = () => import("./pages/Checkout");
const loadCategoryPage = () => import("./pages/CategoryPage");

const ProductDetail = lazy(loadProductDetail);
const Auth = lazy(loadAuth);
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const Checkout = lazy(loadCheckout);
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const OrderProcedure = lazy(() => import("./pages/OrderProcedure"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CategoryPage = lazy(loadCategoryPage);
const AllProducts = lazy(() => import("./pages/AllProducts"));
const NewArrivals = lazy(() => import("./pages/NewArrivals"));
const BestSellingPage = lazy(() => import("./pages/BestSelling"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Reviews = lazy(() => import("./pages/Reviews"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ModeratorSignup = lazy(() => import("./pages/ModeratorSignup"));
const ModeratorPreview = lazy(() => import("./pages/ModeratorPreview"));
const ModeratorAccess = lazy(() => import("./pages/ModeratorAccess"));
const StaffSignup = lazy(() => import("./pages/StaffSignup"));
const AdminRoutes = lazy(() => import("./routes/AdminRoutes"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const PageLoader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const isHeavy = location.pathname.startsWith("/admin") || location.pathname.startsWith("/moderator");
  const timeoutSec = isHeavy ? 15 : 30;

  useEffect(() => {
    setTimedOut(false);
    setElapsed(0);
    const interval = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    const timer = window.setTimeout(() => setTimedOut(true), timeoutSec * 1000);
    return () => { window.clearTimeout(timer); window.clearInterval(interval); };
  }, [location.pathname, location.search, timeoutSec]);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const isModeratorRoute = location.pathname.startsWith("/moderator") || location.pathname.startsWith("/staff-signup");

  if (!timedOut || !isHeavy) {
    const remaining = Math.max(timeoutSec - elapsed, 0);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground animate-pulse">লোড হচ্ছে... ({elapsed}s)</p>
          <p className="text-[10px] text-muted-foreground/70">
            {remaining > 0 ? `আর ~${remaining} সেকেন্ড অপেক্ষা করুন` : "পেজ প্রস্তুত হচ্ছে..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 text-center space-y-3">
        <div className="space-y-1">
          <h2 className="font-heading text-lg text-foreground">লোডিং বেশি সময় নিচ্ছে</h2>
          <p className="text-sm text-muted-foreground">
            {isAdminRoute || isModeratorRoute
              ? "এই পেজটি ওপেন হতে অস্বাভাবিক বেশি সময় লাগছে। আবার চেষ্টা করুন।"
              : "পেজটি ওপেন হতে অস্বাভাবিক বেশি সময় লাগছে। আবার চেষ্টা করুন।"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={() => window.location.reload()} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            আবার চেষ্টা করুন
          </button>
          <button
            onClick={() => navigate(isAdminRoute || isModeratorRoute ? "/auth" : "/", { replace: true })}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground"
          >
            {isAdminRoute || isModeratorRoute ? "লগইনে যান" : "হোমে যান"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RouteChrome = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {!isAdminRoute && <SeoHead />}
      {!isAdminRoute && (
        <Suspense fallback={null}>
          <StorefrontChrome />
        </Suspense>
      )}
    </>
  );
};

const AppBootEffects = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isAdmin = location.pathname.startsWith("/admin");

    if (isAdmin) {
      const warmAdmin = () => {
        import("recharts").catch(() => {});
        import("jspdf").catch(() => {});
      };
      if ("requestIdleCallback" in window) {
        const id = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number; cancelIdleCallback?: (id: number) => void }).requestIdleCallback(warmAdmin, { timeout: 3000 });
        return () => (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id);
      }
      const timer = globalThis.setTimeout(warmAdmin, 1500);
      return () => globalThis.clearTimeout(timer);
    }

    const preloadCritical = () => {
      loadProductDetail();
      loadCategoryPage();
    };
    const timer = globalThis.setTimeout(preloadCritical, 10000);
    return () => globalThis.clearTimeout(timer);
  }, [location.pathname]);

  return null;
};

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname.startsWith("/admin")) return;
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<StorefrontWrapper><IndexPage /></StorefrontWrapper>} />
        <Route path="/product/:slug" element={<StorefrontWrapper><ProductDetail /></StorefrontWrapper>} />
        <Route path="/auth" element={<StorefrontWrapper><Auth /></StorefrontWrapper>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile" element={<StorefrontWrapper><Profile /></StorefrontWrapper>} />
        <Route path="/orders/:id" element={<StorefrontWrapper><OrderDetails /></StorefrontWrapper>} />
        <Route path="/checkout" element={<StorefrontWrapper><Checkout /></StorefrontWrapper>} />
        <Route path="/thank-you" element={<StorefrontWrapper><ThankYou /></StorefrontWrapper>} />
        <Route path="/wishlist" element={<StorefrontWrapper><Wishlist /></StorefrontWrapper>} />
        <Route path="/about" element={<StorefrontWrapper><AboutUs /></StorefrontWrapper>} />
        <Route path="/order-procedure" element={<StorefrontWrapper><OrderProcedure /></StorefrontWrapper>} />
        <Route path="/return-policy" element={<StorefrontWrapper><ReturnPolicy /></StorefrontWrapper>} />
        <Route path="/terms-and-conditions" element={<StorefrontWrapper><TermsAndConditions /></StorefrontWrapper>} />
        <Route path="/privacy-policy" element={<StorefrontWrapper><PrivacyPolicy /></StorefrontWrapper>} />
        <Route path="/category/:slug" element={<StorefrontWrapper><CategoryPage /></StorefrontWrapper>} />
        <Route path="/products" element={<StorefrontWrapper><AllProducts /></StorefrontWrapper>} />
        <Route path="/new-arrivals" element={<StorefrontWrapper><NewArrivals /></StorefrontWrapper>} />
        <Route path="/best-selling" element={<StorefrontWrapper><BestSellingPage /></StorefrontWrapper>} />
        <Route path="/track-order" element={<StorefrontWrapper><TrackOrder /></StorefrontWrapper>} />
        <Route path="/reviews" element={<StorefrontWrapper><Reviews /></StorefrontWrapper>} />
        <Route path="/moderator-signup" element={<ModeratorSignup />} />
        <Route path="/moderator" element={<ModeratorAccess />} />
        <Route path="/staff-signup" element={<StaffSignup />} />
        <Route path="/mod-preview-a7f3e2d1" element={<ModeratorPreview />} />
        {isAdminRoute && <Route path="/admin/*" element={<AdminRoutes />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <SiteThemeProvider>
          <LanguageProvider>
            <CartProvider>
              <AppBootEffects />
              <PageViewTracker />
              <RouteChrome />
              <AppRoutes />
            </CartProvider>
          </LanguageProvider>
        </SiteThemeProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
